/**
 * Session IndexedDB Store
 *
 * localStorage 只存会话索引（id/title/updatedAt），完整数据存入 IndexedDB。
 * IndexedDB 配额通常 50MB-250MB+，远超 localStorage 的 5-10MB。
 */

import type { ChatSession } from '../types';

const DB_NAME = 'realplc_sessions';
const DB_VERSION = 1;
const STORE_NAME = 'sessions';

function isIndexedDBAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}

function openDB(): Promise<IDBDatabase> {
  if (!isIndexedDBAvailable()) {
    return Promise.reject(new Error('IndexedDB not available'));
  }
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error('IndexedDB blocked'));
  });
}

/** 批量写入会话到 IndexedDB（异步，fire-and-forget 安全） */
export async function saveSessionsToIndexedDB(sessions: ChatSession[]): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);

    // 先清空旧数据，再全量写入
    store.clear();

    for (const session of sessions) {
      store.put(session);
    }

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = () => { db.close(); reject(tx.error); };
      tx.onabort = () => { db.close(); reject(tx.error); };
    });
  } catch (err) {
    console.warn('[sessionIndexedDB] save failed, falling back to localStorage only:', err);
  }
}

/** 从 IndexedDB 加载完整会话数据 */
export async function loadSessionsFromIndexedDB(): Promise<ChatSession[]> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        db.close();
        resolve(Array.isArray(request.result) ? request.result : []);
      };
      request.onerror = () => { db.close(); reject(request.error); };
    });
  } catch {
    return [];
  }
}

/** 从 IndexedDB 加载指定会话（按需加载单条） */
export async function loadSessionFromIndexedDB(sessionId: string): Promise<ChatSession | null> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(sessionId);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        db.close();
        resolve(request.result ?? null);
      };
      request.onerror = () => { db.close(); reject(request.error); };
    });
  } catch {
    return null;
  }
}
